<template>
  <v-app>

    <!-- Sidebar -->
    <v-navigation-drawer v-model="drawer" permanent color="#091E42" width="220">

      <!-- Logo -->
      <div class="h-16 flex items-center px-5">
        <img src="/images/all/logos/tracx-logo-white.svg" alt="Tracx" class="h-10 w-auto">
      </div>

      <v-divider class="-mt-[1px]" color="rgba(255,255,255,0.1)" />

      <!-- Nav -->
      <v-list class="pa-0 !bg-transparent">
        <v-list-item
          v-for="item in navItems"
          :key="item.view"
          :active="activeView === item.view"
          class="nav-item"
          @click="activeView = item.view"
        >
          <template #prepend>
            <v-icon size="18" class="nav-icon">{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title>{{ item.label }}</v-list-item-title>
        </v-list-item>
      </v-list>

      <template v-if="isDev">
        <v-divider color="rgba(255,255,255,0.1)" class="mt-auto" />
        <v-list class="pa-0 !bg-transparent">
          <v-list-item
            :active="activeView === 'dev'"
            class="nav-item"
            @click="activeView = 'dev'"
          >
            <template #prepend>
              <v-icon size="18" class="nav-icon">mdi-code-braces</v-icon>
            </template>
            <v-list-item-title>Dev tools</v-list-item-title>
          </v-list-item>
        </v-list>
      </template>

    </v-navigation-drawer>

    <v-main class="bg-[#f0f2f5]">

      <!-- Topbar -->
      <div class="h-16 bg-white border-b border-[#e8edf2] flex items-center justify-between px-6 sticky top-0 z-10">
        <button
          class="flex flex-col justify-center gap-[5px] w-9 h-9 p-[7px] bg-transparent border-none cursor-pointer shrink-0"
          @click="drawer = !drawer"
          aria-label="Menu"
        >
          <span
            class="block w-full h-0.5 bg-[#091E42] rounded-sm transition-all duration-300 ease-in-out origin-center"
            :class="{ 'translate-y-[7px] rotate-45': drawer }"
          />
          <span
            class="block w-full h-0.5 bg-[#091E42] rounded-sm transition-all duration-300 ease-in-out origin-center"
            :class="{ 'opacity-0 scale-x-0': drawer }"
          />
          <span
            class="block w-full h-0.5 bg-[#091E42] rounded-sm transition-all duration-300 ease-in-out origin-center"
            :class="{ '-translate-y-[7px] -rotate-45': drawer }"
          />
        </button>
        <div class="flex items-center gap-3">
          <span class="text-[#5a6a7a] text-[13px] hidden sm:inline">{{ user?.email }}</span>
          <v-chip
            v-if="userRole"
            size="x-small"
            variant="tonal"
            :color="userRole === 'owner' ? '#ff6600' : userRole === 'editor' ? '#1565c0' : '#5a6a7a'"
            class="capitalize hidden sm:flex"
          >{{ userRole }}</v-chip>
          <v-btn
            variant="outlined"
            size="small"
            class="signout-btn"
            @click="handleSignOut"
          >
            <v-icon start size="16">mdi-logout</v-icon>
            Sign out
          </v-btn>
        </div>
      </div>

      <!-- Content -->
      <div class="px-6 py-8 min-h-[calc(100vh-64px)]">

        <!-- Role loading -->
        <div v-if="roleLoading" class="flex items-center justify-center py-32">
          <v-progress-circular size="36" width="3" indeterminate color="#ff6600" />
        </div>

        <!-- No access -->
        <div v-else-if="isUnauthorized" class="flex flex-col items-center justify-center py-32 gap-4 text-center">
          <div class="flex items-center justify-center w-14 h-14 rounded-xl bg-[#ffebee] mb-2">
            <v-icon color="#c62828" size="28">mdi-lock-outline</v-icon>
          </div>
          <h2 class="!text-[22px] !font-semibold !text-[#091E42] !m-0">No access</h2>
          <p class="!text-[#5a6a7a] !text-[14px] !m-0 max-w-xs">
            Your account has no admin role. Ask an administrator to grant you access in the Firebase console (<code>users/{{ user?.uid }}</code>).
          </p>
          <v-btn variant="outlined" rounded="lg" class="signout-btn mt-2" @click="handleSignOut">
            <v-icon start size="16">mdi-logout</v-icon>
            Sign out
          </v-btn>
        </div>

        <template v-else>

        <!-- Actions -->
        <template v-if="activeView === 'acties'">
          <div class="mb-8">
            <h1 class="!text-[28px] !font-semibold !tracking-[-0.6px] !mb-1">Actions</h1>
            <p class="!text-[#5a6a7a] !text-[14px] !m-0">Manage events and submissions</p>
          </div>

          <v-row>
            <v-col v-if="canDelete" cols="12" sm="6" lg="4">
              <v-card rounded="lg" elevation="1" height="100%" class="relative !border !border-[#e8edf2]">
                <div class="absolute top-3 bottom-3 left-0 w-1 rounded-r bg-[#e53935]"></div>
                <v-card-item class="pt-5">
                  <template #prepend>
                    <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-[#ffebee]">
                      <v-icon color="#c62828" size="20">mdi-delete-sweep</v-icon>
                    </div>
                  </template>
                  <v-card-title class="!text-[15px] !font-semibold !text-[#091E42]">Delete submissions</v-card-title>
                  <v-card-subtitle class="!text-[13px] !text-[#6b7a8a] !opacity-100 !whitespace-normal">Remove all submissions for a specific event</v-card-subtitle>
                </v-card-item>
                <v-card-text class="pt-2 pb-0">
                  <v-select
                    v-model="deleteEventId"
                    :items="deleteEventOptions"
                    :loading="eventsLoading"
                    item-value="value"
                    item-title="label"
                    label="Select event"
                    variant="outlined"
                    density="compact"
                    hide-details
                    rounded="lg"
                    no-data-text="No events found"
                  >
                    <template #item="{ item, props }">
                      <v-list-item v-bind="props" class="event-select-item">
                        <template #append>
                          <span class="text-xs opacity-60 ml-4">{{ item.raw.count }} {{ item.raw.count === 1 ? 'submission' : 'submissions' }}</span>
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-card-text>
                <v-card-actions class="!p-4">
                  <v-btn
                    variant="tonal"
                    :disabled="!deleteEventId"
                    rounded="lg"
                    @click="confirmDeleteDialog = true"
                  >
                    <v-icon start size="16">mdi-delete</v-icon>
                    Delete
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" lg="4">
              <v-card rounded="lg" elevation="1" height="100%" class="relative !border !border-[#e8edf2]">
                <div class="absolute top-3 bottom-3 left-0 w-1 rounded-r bg-[#1565c0]"></div>
                <v-card-item class="pt-5">
                  <template #prepend>
                    <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-[#e3f2fd]">
                      <v-icon color="#1565c0" size="20">mdi-eye-refresh</v-icon>
                    </div>
                  </template>
                  <v-card-title class="!text-[15px] !font-semibold !text-[#091E42]">Reset watched</v-card-title>
                  <v-card-subtitle class="!text-[13px] !text-[#6b7a8a] !opacity-100 !whitespace-normal">Set all submissions of an event back to unwatched</v-card-subtitle>
                </v-card-item>
                <v-card-text class="pt-2 pb-0">
                  <v-select
                    v-model="resetWatchedEventId"
                    :items="deleteEventOptions"
                    :loading="eventsLoading"
                    item-value="value"
                    item-title="label"
                    label="Select event"
                    variant="outlined"
                    density="compact"
                    hide-details
                    rounded="lg"
                    no-data-text="No events found"
                  >
                    <template #item="{ item, props }">
                      <v-list-item v-bind="props" class="event-select-item">
                        <template #append>
                          <span class="text-xs opacity-60 ml-4">{{ item.raw.count }} {{ item.raw.count === 1 ? 'submission' : 'submissions' }}</span>
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-card-text>
                <v-card-actions class="!p-4">
                  <v-btn
                    variant="tonal"
                    :disabled="!resetWatchedEventId"
                    :loading="resetWatchedLoading"
                    rounded="lg"
                    @click="handleResetWatched"
                  >
                    <v-icon start size="16">mdi-refresh</v-icon>
                    Reset
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <!-- Analytics -->
        <template v-if="activeView === 'analytics'">
          <div class="mb-6 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 class="!text-[28px] !font-semibold !tracking-[-0.6px] !mb-1">Analytics</h1>
              <p class="!text-[#5a6a7a] !text-[14px] !m-0">Overview of submissions</p>
            </div>
            <v-select
              v-model="analyticsEventId"
              :items="analyticsEventOptions"
              item-value="value"
              item-title="label"
              variant="outlined"
              density="compact"
              hide-details
              rounded="lg"
              style="max-width: 220px"
            >
              <template #item="{ item, props }">
                <v-list-item v-bind="props" class="event-select-item" />
              </template>
            </v-select>
          </div>

          <!-- Loading state -->
          <div v-if="analyticsLoading" class="flex flex-col items-center justify-center py-24 gap-4">
            <v-progress-circular size="40" width="3" indeterminate color="#ff6600" />
            <span class="text-[#5a6a7a] text-sm">Loading data…</span>
          </div>

          <template v-else-if="analytics">

            <!-- KPI cards -->
            <v-row class="mb-2">
              <v-col v-for="kpi in kpiCards" :key="kpi.label" cols="6" sm="3">
                <v-card rounded="lg" elevation="1" class="!border !border-[#e8edf2] px-5 py-4">
                  <div class="text-[12px] text-[#5a6a7a] font-medium mb-1">{{ kpi.label }}</div>
                  <div class="text-[32px] font-bold text-[#091E42] leading-none">{{ kpi.value }}</div>
                  <div v-if="kpi.sub" class="text-[11px] text-[#5a6a7a] mt-1">{{ kpi.sub }}</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Submissions per event: only when all events selected -->
            <v-row v-if="!analyticsEventId" class="mb-2">
              <v-col cols="12">
                <v-card rounded="lg" elevation="1" class="!border !border-[#e8edf2] pa-5">
                  <div class="text-[14px] font-semibold text-[#091E42] mb-4">Submissions per event</div>
                  <apexchart
                    type="bar"
                    height="220"
                    :options="perEventChartOptions"
                    :series="perEventSeries"
                  />
                </v-card>
              </v-col>
            </v-row>

            <!-- Watched vs unwatched + submission type -->
            <v-row>
              <v-col cols="12" :md="analytics.photoStats ? 8 : 12">
                <v-card rounded="lg" elevation="1" class="!border !border-[#e8edf2] pa-5">
                  <div class="text-[14px] font-semibold text-[#091E42] mb-1">Watched vs unwatched per event</div>
                  <div class="flex items-start gap-1 text-[11px] text-[#5a6a7a] mb-4">
                    <v-icon size="12" class="mt-[1px] shrink-0">mdi-information-outline</v-icon>
                    <span>Only reliable if <em>Mark watched</em> was enabled on the cheer screen during the event.</span>
                  </div>
                  <apexchart
                    type="bar"
                    height="220"
                    :options="watchedChartOptions"
                    :series="watchedSeries"
                  />
                </v-card>
              </v-col>
              <v-col v-if="analytics.photoStats" cols="12" md="4">
                <v-card rounded="lg" elevation="1" class="!border !border-[#e8edf2] pa-5">
                  <div class="text-[14px] font-semibold text-[#091E42] mb-4">{{ analyticsEventId ? 'Photo with text vs text only' : 'Submission type' }}</div>
                  <apexchart
                    :key="`photo-${analyticsEventId}`"
                    type="donut"
                    height="220"
                    :options="photoStatsChartOptions"
                    :series="photoStatsSeries"
                  />
                </v-card>
              </v-col>
            </v-row>

            <!-- Submissions list: only for a specific event -->
            <v-row v-if="analyticsEventId" class="mt-0">
              <v-col cols="12">
                <v-card rounded="lg" elevation="1" class="!border !border-[#e8edf2]">

                  <!-- Card header -->
                  <div class="flex items-center justify-between gap-4 flex-wrap px-5 pt-5 pb-4">
                    <div class="text-[14px] font-semibold text-[#091E42]">
                      Submissions
                      <span v-if="!submissionsLoading" class="text-[12px] font-normal text-[#5a6a7a] ml-1">({{ filteredSubmissions.length }})</span>
                    </div>
                    <v-text-field
                      v-model="submissionsSearch"
                      prepend-inner-icon="mdi-magnify"
                      placeholder="Search…"
                      variant="outlined"
                      density="compact"
                      hide-details
                      rounded="lg"
                      clearable
                      style="max-width: 260px"
                      @click:clear="submissionsSearch = ''"
                    />
                  </div>

                  <!-- Loading -->
                  <div v-if="submissionsLoading" class="flex items-center justify-center py-12">
                    <v-progress-circular size="32" width="3" indeterminate color="#ff6600" />
                  </div>

                  <template v-else>
                    <!-- Table -->
                    <div class="overflow-x-auto border-t border-[#e8edf2]">
                      <table class="w-full text-[13px]">
                        <thead>
                          <tr class="bg-[#f8f9fb]">
                            <th
                              v-for="col in submissionsColumns"
                              :key="col.key"
                              class="text-left px-5 py-3 text-[#5a6a7a] font-medium whitespace-nowrap cursor-pointer select-none hover:text-[#091E42] transition-colors"
                              @click="toggleSort(col.key)"
                            >
                              <span class="inline-flex items-center gap-1">
                                {{ col.label }}
                                <span class="inline-flex flex-col leading-none gap-[1px]">
                                  <v-icon
                                    size="11"
                                    :style="{ opacity: submissionsSort.key === col.key && submissionsSort.order === 'asc' ? 1 : 0.25 }"
                                  >mdi-chevron-up</v-icon>
                                  <v-icon
                                    size="11"
                                    :style="{ opacity: submissionsSort.key === col.key && submissionsSort.order === 'desc' ? 1 : 0.25 }"
                                  >mdi-chevron-down</v-icon>
                                </span>
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="paginatedSubmissions.length === 0">
                            <td :colspan="submissionsColumns.length" class="text-center py-10 text-[#5a6a7a] text-[13px]">
                              No submissions found
                            </td>
                          </tr>
                          <tr
                            v-for="row in paginatedSubmissions"
                            :key="row.id"
                            class="border-t border-[#f0f2f5] hover:bg-[#f8f9fb] transition-colors"
                          >
                            <td
                              v-for="col in submissionsColumns"
                              :key="col.key"
                              class="px-5 py-3 text-[#091E42] max-w-[280px] truncate"
                            >
                              <template v-if="col.key === 'watched'">
                                <span
                                  class="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full"
                                  :class="row.watched ? 'bg-[#e8f5e9] text-[#2e7d32]' : 'bg-[#f5f5f5] text-[#757575]'"
                                >
                                  <span class="w-1.5 h-1.5 rounded-full shrink-0" :class="row.watched ? 'bg-[#2e7d32]' : 'bg-[#bdbdbd]'" />
                                  {{ row.watched ? 'Watched' : 'Unwatched' }}
                                </span>
                              </template>
                              <template v-else>
                                {{ submissionCellValue(row, col.key) }}
                              </template>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Pagination -->
                    <div class="flex items-center justify-between px-5 py-3 border-t border-[#e8edf2] text-[12px] text-[#5a6a7a]">
                      <span>
                        {{ sortedSubmissions.length === 0 ? '0' : (submissionsPage - 1) * submissionsPerPage + 1 }}–{{ Math.min(submissionsPage * submissionsPerPage, sortedSubmissions.length) }}
                        of {{ sortedSubmissions.length }}
                      </span>
                      <div class="flex items-center gap-1">
                        <v-btn
                          icon
                          size="x-small"
                          variant="text"
                          :disabled="submissionsPage <= 1"
                          @click="submissionsPage--"
                        >
                          <v-icon size="18">mdi-chevron-left</v-icon>
                        </v-btn>
                        <span class="px-2">{{ submissionsPage }} / {{ submissionsTotalPages }}</span>
                        <v-btn
                          icon
                          size="x-small"
                          variant="text"
                          :disabled="submissionsPage >= submissionsTotalPages"
                          @click="submissionsPage++"
                        >
                          <v-icon size="18">mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </template>

                </v-card>
              </v-col>
            </v-row>

          </template>
        </template>

        <!-- Dev tools -->
        <template v-if="activeView === 'dev' && isDev">
          <div class="mb-8">
            <h1 class="!text-[28px] !font-semibold !tracking-[-0.6px] !mb-1">Dev tools</h1>
            <p class="!text-[#5a6a7a] !text-[14px] !m-0">Only visible in development mode</p>
          </div>

          <v-row>
            <v-col cols="12" sm="6" lg="4">
              <v-card rounded="lg" elevation="1" class="relative !border !border-[#e8edf2]">
                <div class="absolute top-3 bottom-3 left-0 w-1 rounded-r bg-[#f57c00]"></div>
                <v-card-item class="pt-5">
                  <template #prepend>
                    <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-[#fff3e0]">
                      <v-icon color="#e65100" size="20">mdi-database-sync</v-icon>
                    </div>
                  </template>
                  <v-card-title class="!text-[15px] !font-semibold !text-[#091E42]">Sync live → local</v-card-title>
                  <v-card-subtitle class="!text-[13px] !text-[#6b7a8a] !opacity-100 !whitespace-normal">Overwrites all local submissions with live Firebase data</v-card-subtitle>
                </v-card-item>
                <v-card-text class="pt-2 pb-0">
                  <div v-if="syncStatus" class="flex items-center gap-2 text-[13px]" :class="syncStatus.error ? 'text-red-600' : 'text-[#5a6a7a]'">
                    <v-progress-circular v-if="syncLoading" size="13" width="2" indeterminate color="#5a6a7a" />
                    <v-icon v-else-if="syncStatus.error" size="14" color="error">mdi-alert-circle</v-icon>
                    <v-icon v-else size="14" color="success">mdi-check-circle</v-icon>
                    <span>{{ syncStatus.message }}</span>
                  </div>
                </v-card-text>
                <v-card-actions class="!p-4">
                  <v-btn
                    variant="tonal"
                    rounded="lg"
                    :loading="syncLoading"
                    @click="handleSync"
                  >
                    <v-icon start size="16">mdi-sync</v-icon>
                    Synchronise
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </template>

        </template><!-- end role-authorized -->
      </div>
    </v-main>

    <!-- Confirm delete dialog -->
    <v-dialog v-model="confirmDeleteDialog" max-width="440" @after-leave="confirmText = ''">
      <v-card rounded="lg">
        <v-card-item class="pt-6">
          <template #prepend>
            <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-[#ffebee]">
              <v-icon color="#c62828" size="20">mdi-alert</v-icon>
            </div>
          </template>
          <v-card-title>Confirm deletion</v-card-title>
        </v-card-item>
        <v-card-text>
          <p class="mb-4">
            Are you sure you want to delete <strong>all submissions</strong> for event
            <strong>{{ deleteEventId }}</strong>?
            This action cannot be undone.
          </p>
          <p class="!text-[13px] !text-[#5a6a7a] mb-2">Type <strong>DELETE</strong> to confirm</p>
          <v-text-field
            v-model="confirmText"
            variant="outlined"
            density="compact"
            hide-details
            rounded="lg"
            placeholder="DELETE"
            autofocus
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-5 ga-2">
          <v-spacer />
          <v-btn variant="text" rounded="lg" @click="confirmDeleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            variant="flat"
            rounded="lg"
            :loading="deleteLoading"
            :disabled="confirmText !== 'DELETE'"
            @click="handleDelete"
          >
            Yes, delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Status notification -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="4000"
      location="bottom right"
      rounded="lg"
    >
      {{ snackbar.message }}
    </v-snackbar>

  </v-app>
</template>

<script>
import { adminSignOut, onAdminAuthStateChanged, getUserAccess, deleteSubmissionsByEventId, resetWatchedSubmissions, getUniqueEventIds, syncLiveToLocal, getAnalyticsData, getSubmissionsByEventId } from '@/firebase';

const ORANGE = '#ff6600';
const BLUE   = '#091E42';
const GRAY   = '#b0bec5';

const baseChart = {
  toolbar: { show: false },
  fontFamily: 'inherit',
};

// Role → permissions mapping. Adjust here to change what each role can do.
const ROLE_PERMISSIONS = {
  owner:  { views: ['analytics', 'acties'], canDelete: true },
  editor: { views: ['analytics'],           canDelete: false },
  viewer: { views: ['analytics'],           canDelete: false },
};

const ALL_NAV_ITEMS = [
  { view: 'analytics', label: 'Analytics', icon: 'mdi-chart-bar' },
  { view: 'acties',    label: 'Actions',   icon: 'mdi-lightning-bolt' },
];

export default {
  name: 'AdminDashboardPage',

  data() {
    return {
      user: null,
      userRole: null,
      userEvents: 'all',
      roleLoading: true,
      drawer: true,
      activeView: 'analytics',
      isDev: process.env.NODE_ENV === 'development',

      // Actions
      eventIds: [],
      eventsLoading: false,
      deleteEventId: null,
      confirmDeleteDialog: false,
      confirmText: '',
      deleteLoading: false,
      resetWatchedEventId: null,
      resetWatchedLoading: false,

      // Dev
      syncLoading: false,
      syncStatus: null,

      // Analytics
      analyticsLoading: false,
      analytics: null,
      analyticsEventId: null,

      // Submissions list
      submissionsData: [],
      submissionsLoading: false,
      submissionsSearch: '',
      submissionsPage: 1,
      submissionsPerPage: 20,
      submissionsSort: { key: 'start_number', order: 'asc' },

      snackbar: { show: false, message: '', color: 'success' },
    };
  },

  computed: {
    navItems() {
      const perms = ROLE_PERMISSIONS[this.userRole];
      if (!perms) return [];
      return ALL_NAV_ITEMS.filter(item => perms.views.includes(item.view));
    },
    canDelete() {
      return ROLE_PERMISSIONS[this.userRole]?.canDelete ?? false;
    },
    isUnauthorized() {
      return !this.roleLoading && !this.userRole;
    },

    allowedEventIds() {
      if (!this.userEvents || this.userEvents === 'all') return this.eventIds;
      return this.eventIds.filter(e => this.userEvents.includes(e.id));
    },

    analyticsEventOptions() {
      const events = this.allowedEventIds.map(e => ({ value: e.id, label: `Event ${e.id}` }));
      if (this.userEvents === 'all') return [{ value: null, label: 'All events' }, ...events];
      return events;
    },

    deleteEventOptions() {
      return this.allowedEventIds.map(e => ({ value: e.id, label: e.id, count: e.count }));
    },

    kpiCards() {
      if (!this.analytics) return [];
      const a = this.analytics;
      return [
        { label: 'Total submissions',      value: a.total },
        ...(!this.analyticsEventId ? [{ label: 'Events', value: a.eventCount }] : []),
        { label: 'Participants with submission', value: a.uniqueRunners, sub: 'based on bib number' },
        { label: 'Watched',                value: `${a.watchedPct}%`, sub: `${a.total - Math.round(a.total * a.watchedPct / 100)} not yet watched` },
      ];
    },

    perEventSeries() {
      if (!this.analytics) return [];
      return [{ name: 'Submissions', data: this.analytics.perEvent.totals }];
    },
    perEventChartOptions() {
      const labels = this.analytics?.perEvent.labels ?? [];
      return {
        chart: { ...baseChart, type: 'bar' },
        colors: [ORANGE],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
        dataLabels: { enabled: false },
        xaxis: { categories: labels, labels: { style: { colors: '#5a6a7a', fontSize: '12px' } } },
        yaxis: { labels: { style: { colors: '#5a6a7a', fontSize: '12px' } } },
        grid: { borderColor: '#f0f2f5' },
        tooltip: { theme: 'light' },
      };
    },

    photoStatsSeries() {
      if (!this.analytics?.photoStats) return [];
      const { withVideo, withPhoto, textOnly } = this.analytics.photoStats;
      return this.analyticsEventId
        ? [withPhoto, textOnly]
        : [withVideo, withPhoto, textOnly];
    },
    photoStatsChartOptions() {
      const labels = this.analyticsEventId
        ? ['Photo with text', 'Text only']
        : ['Video', 'Photo with text', 'Text only'];
      const colors = this.analyticsEventId
        ? [ORANGE, GRAY]
        : [BLUE, ORANGE, GRAY];
      return {
        chart: { ...baseChart, type: 'donut' },
        colors,
        labels,
        legend: { position: 'bottom', fontSize: '12px', labels: { colors: '#5a6a7a' } },
        dataLabels: { style: { fontSize: '12px' } },
        plotOptions: { pie: { donut: { size: '65%' } } },
        tooltip: { theme: 'light' },
      };
    },

    watchedSeries() {
      if (!this.analytics) return [];
      return [
        { name: 'Watched',   data: this.analytics.perEvent.watched },
        { name: 'Unwatched', data: this.analytics.perEvent.unwatched },
      ];
    },
    watchedChartOptions() {
      const labels = this.analytics?.perEvent.labels ?? [];
      return {
        chart: { ...baseChart, type: 'bar', stacked: true },
        colors: [ORANGE, GRAY],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
        dataLabels: { enabled: false },
        xaxis: { categories: labels, labels: { style: { colors: '#5a6a7a', fontSize: '12px' } } },
        yaxis: { labels: { style: { colors: '#5a6a7a', fontSize: '12px' } } },
        legend: { position: 'top', fontSize: '12px', labels: { colors: '#5a6a7a' } },
        grid: { borderColor: '#f0f2f5' },
        tooltip: { theme: 'light' },
      };
    },

    isPhotoEvent() {
      return this.analytics !== null && this.analytics.photoStats !== null;
    },

    submissionsColumns() {
      const base = [
        { key: 'start_number', label: 'Bib #' },
        { key: 'name',         label: 'Participant' },
        { key: 'watched',      label: 'Watched' },
      ];
      if (this.isPhotoEvent) {
        return [
          { key: 'start_number', label: 'Bib #' },
          { key: 'name',         label: 'Participant' },
          { key: 'message',      label: 'Message' },
          { key: 'name_cheerer', label: 'Cheerer' },
          { key: 'watched',      label: 'Watched' },
        ];
      }
      return base;
    },

    filteredSubmissions() {
      const q = (this.submissionsSearch || '').trim().toLowerCase();
      if (!q) return this.submissionsData;
      return this.submissionsData.filter(row => {
        return [
          row.start_number,
          row.name_participant || row.name,
          row.message,
          row.name_cheerer,
          row.watched ? 'watched' : 'unwatched',
        ].some(v => v != null && String(v).toLowerCase().includes(q));
      });
    },

    sortedSubmissions() {
      const { key, order } = this.submissionsSort;
      if (!key) return this.filteredSubmissions;
      return [...this.filteredSubmissions].sort((a, b) => {
        let aVal = key === 'name' ? (a.name_participant || a.name || '') : (a[key] ?? '');
        let bVal = key === 'name' ? (b.name_participant || b.name || '') : (b[key] ?? '');
        if (typeof aVal === 'boolean') aVal = aVal ? 1 : 0;
        if (typeof bVal === 'boolean') bVal = bVal ? 1 : 0;
        const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
        return order === 'asc' ? cmp : -cmp;
      });
    },

    paginatedSubmissions() {
      const start = (this.submissionsPage - 1) * this.submissionsPerPage;
      return this.sortedSubmissions.slice(start, start + this.submissionsPerPage);
    },

    submissionsTotalPages() {
      return Math.max(1, Math.ceil(this.sortedSubmissions.length / this.submissionsPerPage));
    },
  },

  watch: {
    activeView(view) {
      if (view === 'analytics') {
        if (!this.analytics) this.loadAnalytics();
        if (this.analyticsEventId && !this.submissionsData.length) this.loadSubmissions();
      }
    },
    analyticsEventId() {
      if (this.activeView === 'analytics') {
        this.submissionsSearch = '';
        this.submissionsPage = 1;
        this.submissionsSort = { key: 'start_number', order: 'asc' };
        this.loadAnalytics();
        if (this.analyticsEventId) {
          this.loadSubmissions();
        } else {
          this.submissionsData = [];
        }
      }
    },
    submissionsSearch() {
      this.submissionsPage = 1;
    },
  },

  created() {
    if (process.env.NODE_ENV === 'development') {
      this.user = { email: 'dev@localhost' };
      this.userRole = 'owner';
      this.userEvents = 'all';
      this.roleLoading = false;
      this.loadEventIds().then(() => {
        if (this.userEvents !== 'all' && this.allowedEventIds.length > 0) {
          this.analyticsEventId = this.allowedEventIds[0].id;
        }
        this.loadAnalytics();
        if (this.analyticsEventId) this.loadSubmissions();
      });
      return;
    }
    this.unsubscribe = onAdminAuthStateChanged(async (user) => {
      if (!user || user.isAnonymous) {
        this.$router.replace('/admin');
        return;
      }
      this.user = user;
      if (this.roleLoading) {
        const { role, events } = await getUserAccess(user.uid);
        this.userRole = role;
        this.userEvents = events;
        this.roleLoading = false;
        if (this.userRole) {
          await this.loadEventIds();
          if (this.userEvents !== 'all' && this.allowedEventIds.length > 0) {
            this.analyticsEventId = this.allowedEventIds[0].id;
          }
          this.loadAnalytics();
          if (this.analyticsEventId) this.loadSubmissions();
        }
      }
    });
  },

  unmounted() {
    this.unsubscribe?.();
  },

  methods: {
    async handleSignOut() {
      await adminSignOut();
      this.$router.replace('/admin');
    },

    async loadEventIds() {
      this.eventsLoading = true;
      try {
        this.eventIds = await getUniqueEventIds();
      } catch (e) {
        this.showSnackbar('Failed to load events.', 'error');
      } finally {
        this.eventsLoading = false;
      }
    },

    async loadAnalytics() {
      this.analyticsLoading = true;
      this.analytics = null;
      try {
        this.analytics = await getAnalyticsData(this.analyticsEventId);
      } catch (e) {
        this.showSnackbar('Failed to load analytics.', 'error');
      } finally {
        this.analyticsLoading = false;
      }
    },

    async loadSubmissions() {
      if (!this.analyticsEventId) { this.submissionsData = []; return; }
      this.submissionsLoading = true;
      this.submissionsData = [];
      try {
        this.submissionsData = await getSubmissionsByEventId(this.analyticsEventId);
      } catch (e) {
        this.showSnackbar('Failed to load submissions.', 'error');
      } finally {
        this.submissionsLoading = false;
      }
    },

    toggleSort(key) {
      if (this.submissionsSort.key === key) {
        this.submissionsSort = { key, order: this.submissionsSort.order === 'asc' ? 'desc' : 'asc' };
      } else {
        this.submissionsSort = { key, order: 'asc' };
      }
      this.submissionsPage = 1;
    },

    submissionCellValue(row, key) {
      if (key === 'name') return row.name_participant || row.name || '—';
      const v = row[key];
      return v != null && v !== '' ? v : '—';
    },

    async handleDelete() {
      this.deleteLoading = true;
      try {
        const count = await deleteSubmissionsByEventId(this.deleteEventId);
        this.confirmDeleteDialog = false;
        this.deleteEventId = null;
        this.analytics = null;
        await this.loadEventIds();
        this.showSnackbar(`${count} ${count === 1 ? 'submission' : 'submissions'} deleted.`, 'success');
      } catch (e) {
        this.showSnackbar('Deletion failed. Check your permissions.', 'error');
      } finally {
        this.deleteLoading = false;
      }
    },

    async handleResetWatched() {
      this.resetWatchedLoading = true;
      try {
        await resetWatchedSubmissions(this.resetWatchedEventId);
        this.resetWatchedEventId = null;
        this.analytics = null;
        this.showSnackbar('All submissions reset to unwatched.', 'success');
      } catch (e) {
        this.showSnackbar('Reset failed. Check your permissions.', 'error');
      } finally {
        this.resetWatchedLoading = false;
      }
    },

    async handleSync() {
      this.syncLoading = true;
      this.syncStatus = { message: 'Fetching live data…' };
      try {
        const result = await syncLiveToLocal((progress) => {
          this.syncStatus = { message: progress.message };
        });
        this.syncStatus = {
          message: `Done — ${result.written} submissions synced (${result.deleted} local deleted).`,
        };
        this.analytics = null;
      } catch (e) {
        this.syncStatus = { message: e.message || 'Sync failed.', error: true };
      } finally {
        this.syncLoading = false;
      }
    },

    showSnackbar(message, color) {
      this.snackbar = { show: true, message, color };
    },
  },
};
</script>

<style>
/* Nav item: Vuetify internals */
.nav-item {
  padding: 10px 20px !important;
  border-radius: 0 !important;
  margin: 0 !important;
  min-height: 44px !important;
}
.nav-item.v-list-item--active {
  background: #1565c0 !important;
}
.nav-item .v-list-item__overlay {
  display: none !important;
}
.nav-item:not(.v-list-item--active) {
  background: transparent !important;
}
.nav-icon {
  color: white;
  margin-right: 12px;
}
.nav-item .v-list-item-title {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: white !important;
}
.nav-item.v-list-item--active .v-list-item-title {
  font-weight: 600 !important;
}

/* Sign-out button: override global orange v-btn */
.signout-btn {
  font-size: 13px !important;
  font-weight: 400 !important;
  color: #091E42 !important;
  border-color: #d0d7de !important;
  background: transparent !important;
  text-transform: none !important;
  height: auto !important;
}
.signout-btn:hover {
  background: rgba(9, 30, 66, 0.04) !important;
  box-shadow: none !important;
}
.signout-btn .v-btn__overlay {
  display: none !important;
}

/* Event dropdown active state (manual class for custom slot items) */
.event-select-item--active {
  background: #ff6600 !important;
  color: white !important;
}
.event-select-item--active * {
  color: white !important;
  opacity: 1 !important;
}
.event-select-item--active .v-list-item__overlay {
  display: none !important;
}

/* Event dropdown: hide overlay globally, restore subtle hover */
.event-select-item .v-list-item__overlay {
  display: none !important;
}
.event-select-item:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}

/* Event dropdown active state */
.event-select-item.v-list-item--active {
  background: #ff6600 !important;
  color: white !important;
}
.event-select-item.v-list-item--active * {
  color: white !important;
  opacity: 1 !important;
}
.event-select-item.v-list-item--active:hover {
  background: #e65c00 !important;
}
</style>
